import { z } from 'zod'
import { AppError } from '@/shared/domain/AppError'
import type { QuoteParty, QuoteRecord, QuoteStatus } from '../../domain/quote'

const relatedLeadSchema = z.object({
  numero: z.string().nullable(),
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  telephone: z.string().nullable(),
  courriel: z.string().nullable(),
  adresse: z.string().nullable().optional(),
  ville: z.string().nullable().optional(),
  province: z.string().nullable().optional(),
  code_postal: z.string().nullable().optional(),
})

const relatedClientSchema = relatedLeadSchema.extend({ entreprise: z.string().nullable() })

export const legacyQuoteRowSchema = z.object({
  id: z.string(),
  numero: z.string().nullable(),
  lead_id: z.string().nullable(),
  client_id: z.string().nullable(),
  montant: z.coerce.number(),
  taxes: z.coerce.number(),
  total: z.coerce.number(),
  version: z.number().int().positive().optional(),
  organization_id: z.string().nullable().optional(),
  statut: z.enum(['brouillon', 'prete', 'envoyee', 'acceptee', 'refusee', 'expiree']),
  expiration: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
  competiteur: z.string().nullable().optional(),
  contract: z
    .array(z.object({ id: z.string(), numero: z.string().nullable() }))
    .nullable()
    .optional(),
  lead: relatedLeadSchema.nullable().optional(),
  client: relatedClientSchema.nullable().optional(),
})

export type LegacyQuoteRow = z.infer<typeof legacyQuoteRowSchema>

const statusMap: Record<LegacyQuoteRow['statut'], QuoteStatus> = {
  brouillon: 'DRAFT',
  prete: 'READY',
  envoyee: 'SENT',
  acceptee: 'ACCEPTED',
  refusee: 'REJECTED',
  expiree: 'EXPIRED',
}

function optionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized === '' ? undefined : normalized
}

function moneyToCents(value: number, field: string, quoteId: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new AppError('UNEXPECTED_ERROR', 'Une soumission legacy possède un montant invalide.', {
      quoteId,
      field,
    })
  }
  return Math.round(value * 100)
}

function contactEmail(value: string | null | undefined): string | undefined {
  return optionalText(value)?.toLocaleLowerCase('fr-CA')
}

function mapAddress(row: z.infer<typeof relatedLeadSchema>): QuoteParty['address'] {
  const line1 = optionalText(row.adresse)
  if (!line1) return undefined
  const city = optionalText(row.ville)
  const province = optionalText(row.province)
  const postalCode = optionalText(row.code_postal)
  return {
    line1,
    ...(city ? { city } : {}),
    ...(province ? { province } : {}),
    ...(postalCode ? { postalCode } : {}),
  }
}

function mapParty(row: LegacyQuoteRow): QuoteParty {
  if (row.client) {
    const reference = optionalText(row.client.numero)
    const phone = optionalText(row.client.telephone)
    const email = contactEmail(row.client.courriel)
    const address = mapAddress(row.client)
    return {
      kind: 'CLIENT',
      displayName:
        (optionalText(row.client.entreprise) ??
          [optionalText(row.client.prenom), optionalText(row.client.nom)].filter(Boolean).join(' ')) ||
        'Client sans nom',
      ...(reference ? { reference } : {}),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
      ...(address ? { address } : {}),
    }
  }
  if (row.lead) {
    const reference = optionalText(row.lead.numero)
    const phone = optionalText(row.lead.telephone)
    const email = contactEmail(row.lead.courriel)
    const address = mapAddress(row.lead)
    return {
      kind: 'LEAD',
      displayName:
        [optionalText(row.lead.prenom), optionalText(row.lead.nom)].filter(Boolean).join(' ') ||
        'Lead sans nom',
      ...(reference ? { reference } : {}),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
      ...(address ? { address } : {}),
    }
  }
  return { kind: 'UNLINKED', displayName: 'Destinataire non lié' }
}

export function mapLegacyQuote(row: LegacyQuoteRow): QuoteRecord {
  const number = optionalText(row.numero)
  if (!number) {
    throw new AppError('UNEXPECTED_ERROR', 'Une soumission legacy ne possède pas de numéro.', {
      quoteId: row.id,
    })
  }
  const validUntil = optionalText(row.expiration)
  const notes = optionalText(row.notes)
  const competitor = optionalText(row.competiteur)
  return {
    id: row.id,
    number,
    status: statusMap[row.statut],
    subtotalCents: moneyToCents(row.montant, 'montant', row.id),
    taxTotalCents: moneyToCents(row.taxes, 'taxes', row.id),
    totalCents: moneyToCents(row.total, 'total', row.id),
    version: row.version ?? 1,
    ...(validUntil ? { validUntil } : {}),
    ...(notes ? { notes } : {}),
    ...(row.lead_id ? { leadId: row.lead_id } : {}),
    ...(row.client_id ? { clientId: row.client_id } : {}),
    ...(competitor ? { competitor } : {}),
    ...(row.contract?.[0]
      ? {
          contractId: row.contract[0].id,
          ...(row.contract[0].numero ? { contractNumber: row.contract[0].numero } : {}),
        }
      : {}),
    party: mapParty(row),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}
